import React, { useEffect, useState } from "react";

import { ISchoolsTreeProps, ITreeItem } from "../../interfaces/tree";
import { EtySchool } from "../../interfaces/entities";

import { setItemsRecursive, resetSelected, findItemRecursive, removeTemporaryItems } from "../../helpers/tree";

import { Icons } from "../../data/Assets";

import TreeItem from "../reusables/TreeItem";
import { SVGIcon } from "../wrappers/SVGIcon";

const LAST_LEVEL = 0;
const SchoolsTree = (props: ISchoolsTreeProps) => {
    const { schools, selected, setSelected, expanded, setExpanded } = props;

    const [items, setItems] = useState<ITreeItem[]>([]);

    useEffect(() => {
        if (schools.length > 0) {
            setItems(setItemsRecursive({
                type: 'school',
                items: schools,
                onClickItem: onClickItem,
                selected: selected,
                expanded: expanded,
                parentMethod: null,
                lastLevel: LAST_LEVEL,
            }));
        } else {
            setItems([]);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schools]);

    const onClickItem = (item: ITreeItem) => {
        let cExpanded = false;
        setExpanded(prevData => {
            const newData = [...prevData];

            const eItemIdx = newData.findIndex(itm => itm.id === item.id && itm.level === item.level);
            if (eItemIdx === -1) {
                newData.push({
                    id: item.id,
                    level: item.level,
                });
                cExpanded = true;
            } else {
                const itm = findItemRecursive(items, item.id, item.level);
                if (itm?.selected) {
                    newData.splice(eItemIdx, 1);
                    cExpanded = false;
                } else {
                    cExpanded = true;
                };
            };

            return newData;
        });

        setSelected({
            id: item.id,
            level: item.level,
            data: item.data,
        });

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);

            const itm = findItemRecursive(newItems, item.id, item.level);
            if (itm) {
                itm.selected = true;
                itm.expanded = cExpanded;
            };

            return newItems;
        });
    };

    const getDisabled = (type: 'school') => {
        if (selected?.id === 0) return true;

        if (type === 'school') {
            return false;
        }

        return true;
    };

    useEffect(() => {
        if (!selected || selected.id > 0) {
            setItems(prevState => {
                return removeTemporaryItems([...prevState]);
            });

            setExpanded(prevData => {
                return prevData.filter(item => item.id > 0);
            });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const onAddNewSchool = () => {
        if (getDisabled('school')) return;

        setItems(prevState => {
            return removeTemporaryItems([...prevState]);
        });

        const data: EtySchool = {
            id: 0,
            name: '',
            status: 'setup',
            status_change: '',
            dli: '',
            url: '',
            domain: '',
            logo_asset_name: '',
            support_email: '',
        };

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);
            newItems.unshift({
                type: 'school',
                id: data.id,
                level: 0,
                name: "New School",
                method: null,
                parentMethod: null,
                children: [],
                data: data,
                selected: true,
                expanded: false,
                onClickItem: () => { },
                lastLevel: LAST_LEVEL,
            });
            return newItems;
        });
        setSelected({
            id: data.id,
            level: 0,
            data: data,
        });
    };

    return (
        <div className="immersion_admin__tree_component">
            <div className="immersion_admin__tree_top">
                <button
                    className={`immersion_admin__tree__action_btn ${getDisabled('school') ? 'immersion_admin__tree__action_btn__disabled' : ''}`}
                    onClick={onAddNewSchool}
                >
                    <SVGIcon src={Icons.Plus} />
                    <span>School</span>
                </button>
            </div>
            <div className="immersion_admin__tree_body">
                {items.length > 0
                    ? items.map((item, index) =>
                        <TreeItem
                            type='school'
                            key={item.id}
                            id={item.id}
                            level={item.level}
                            name={item.name}
                            method={null}
                            parentMethod={null}
                            onClickItem={onClickItem}
                            data={item.data}
                            selected={item.selected}
                            expanded={item.expanded}
                            lastLevel={LAST_LEVEL}
                        >
                            {item.children}
                        </TreeItem>
                    )
                    : <div className="immersion_admin__tree__empty">No schools yet.</div>
                }
            </div>
        </div>
    );
};

export default SchoolsTree;